import { Link } from "react-router-dom";
import { Shield, Eye, Smartphone, FileText, Mail, MapPin } from "lucide-react";
import styles from "./LegalPage.module.css";

const PrivacyPolicy = () => {
  return (
    <div className={styles.legalContainer}>
      <header className={styles.legalHeader}>
        <div className={styles.headerContent}>
          <div className={styles.brandRow}>
            <div className={styles.logoBadge}>
              <Shield size={24} className={styles.logoIcon} />
            </div>
            <div>
              <h1 className={styles.title}>Privacy Policy</h1>
              <p className={styles.subtitle}>My AsmitA — Smart Home IoT & Society Operations Platform</p>
            </div>
          </div>
          <div className={styles.headerMeta}>
            <span className={styles.metaBadge}>Last Updated: October 2026</span>
            <span className={styles.metaBadge}>Version 1.0 (Production)</span>
          </div>
        </div>
      </header>

      <main className={styles.legalBody}>
        <div className={styles.introCard}>
          <p>
            Welcome to <strong>My AsmitA</strong> (&quot;we&quot;, &quot;our&quot;, &quot;us&quot;, or the &quot;Platform&quot;). 
            We are dedicated to safeguarding the privacy, safety, and digital security of all residents, flat owners, tenants, 
            facility administrators, and security personnel who access or utilize the My AsmitA mobile client applications and administrative web portals.
          </p>
          <p>
            This Privacy Policy sets forth our transparent practices regarding the collection, handling, storage, disclosure, and protection of personal data 
            in accordance with the Information Technology Act, 2000, the Digital Personal Data Protection Act (DPDP), and Google Play Developer Program Policies.
          </p>
        </div>

        {/* Section 1 */}
        <section className={styles.section}>
          <h2 className={styles.sectionHeading}>
            <span className={styles.sectionNumber}>1</span> Information We Collect
          </h2>
          <div className={styles.contentBlock}>
            <p>To enable intelligent gate clearance, society management, and resident authorization workflows, we collect the following categories of information:</p>
            
            <div className={styles.cardGrid}>
              <div className={styles.infoCard}>
                <div className={styles.cardIconWrap}>
                  <FileText size={20} />
                </div>
                <h3>A. Resident & User Profile Data</h3>
                <ul>
                  <li><strong>Full Name, Mobile Number, and Email Address</strong> for profile authentication, OTP generation, and official society notifications.</li>
                  <li><strong>Unit & Property Details:</strong> Wing, tower, flat/unit number, floor allocation, and residency status (Owner, Tenant, or Family Member).</li>
                  <li><strong>Registered Vehicles:</strong> Vehicle type (2-wheeler, 4-wheeler) and vehicle registration number for automatic parking and gate clearance.</li>
                </ul>
              </div>

              <div className={styles.infoCard}>
                <div className={styles.cardIconWrap}>
                  <Eye size={20} />
                </div>
                <h3>B. Visitor & Gate Clearance Data</h3>
                <ul>
                  <li><strong>Visitor Information:</strong> Name, phone number, vehicle registration number, photograph captured at the security booth, and visit purpose (guest, delivery, cab, or maintenance).</li>
                  <li><strong>Entry/Exit Logs:</strong> Timestamped gate records, entry point, security guard identifier, host resident approval status, and pass code hashes.</li>
                </ul>
              </div>

              <div className={styles.infoCard}>
                <div className={styles.cardIconWrap}>
                  <Smartphone size={20} />
                </div>
                <h3>C. Device & Technical Diagnostics</h3>
                <ul>
                  <li><strong>Device Metadata:</strong> Device model, operating system version, unique app installation identifiers, and crash diagnostics (processed securely via Firebase Crashlytics) to ensure uninterrupted 24/7 service uptime.</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Section 2 */}
        <section className={styles.section}>
          <h2 className={styles.sectionHeading}>
            <span className={styles.sectionNumber}>2</span> Device Permissions Requested & Justification
          </h2>
          <div className={styles.contentBlock}>
            <p>Our mobile application strictly requests runtime device permissions only when essential for core functionality:</p>
            <div className={styles.permissionTableWrap}>
              <table className={styles.permissionTable}>
                <thead>
                  <tr>
                    <th>Permission</th>
                    <th>Required For</th>
                    <th>Storage & Transmission</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Camera</strong></td>
                    <td>
                      Security guard verification of visitor badges, gate photo capture of delivery/guests, 
                      and attaching photo evidence to maintenance helpdesk tickets.
                    </td>
                    <td>Encrypted transmission over HTTPS; never accessed in background without user action.</td>
                  </tr>
                  <tr>
                    <td><strong>Storage / Media Photos</strong></td>
                    <td>
                      Uploading digital gate pass QR codes, downloading society maintenance receipts, 
                      and uploading profile or document attachments.
                    </td>
                    <td>Strictly scoped to user-selected media and pass downloads.</td>
                  </tr>
                  <tr>
                    <td><strong>Notifications</strong></td>
                    <td>
                      Delivering real-time gate arrival alerts, visitor clearance requests, emergency broadcasts, 
                      and society circulars directly to residents.
                    </td>
                    <td>Encrypted Firebase Cloud Messaging (FCM) push tokens.</td>
                  </tr>
                  <tr>
                    <td><strong>Microphone (Optional)</strong></td>
                    <td>
                      Enables walkie-talkie style audio messaging in official society community chats and inter-gate security communication.
                    </td>
                    <td>Audio recordings are transient and only transmitted when the user holds the record button.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Section 3 */}
        <section className={styles.section}>
          <h2 className={styles.sectionHeading}>
            <span className={styles.sectionNumber}>3</span> How We Use Your Information
          </h2>
          <div className={styles.contentBlock}>
            <p>We process your data strictly to deliver society operations and security services:</p>
            <ul className={styles.bulletList}>
              <li><strong>Verify and authorize gate entries:</strong> Allowing residents to grant instant gate access to guests, deliveries, and cabs with single-use tokens.</li>
              <li><strong>Community security auditing:</strong> Providing society management committees and certified guards with tamper-evident records of community vehicular and pedestrian movement.</li>
              <li><strong>Amenity and facility bookings:</strong> Administering shared society facilities (clubhouses, sports venues, banquet halls) with zero conflict.</li>
              <li><strong>Emergency communications:</strong> Dispatching urgent society broadcasts, maintenance circulars, and security alerts.</li>
            </ul>
          </div>
        </section>

        {/* Section 4 */}
        <section className={styles.section}>
          <h2 className={styles.sectionHeading}>
            <span className={styles.sectionNumber}>4</span> Data Sharing & Third-Party Service Providers
          </h2>
          <div className={styles.contentBlock}>
            <p>
              <strong>We never sell, rent, or monetize your personal or residency data.</strong> Data is shared only with verified service partners necessary for platform operations:
            </p>
            <ul className={styles.bulletList}>
              <li><strong>SMS Gateway Providers (DLT Regulated):</strong> Transmitting one-time passwords (OTP) and official onboarding invitations under registered telecom templates.</li>
              <li><strong>Cloud Infrastructure & Database Hosting:</strong> ISO-certified, enterprise-grade cloud servers maintaining strict end-to-end encryption in transit (TLS 1.3) and at rest (AES-256).</li>
              <li><strong>Authorized Society Security & Management Committees:</strong> Designated society administrators and on-duty security personnel strictly within your registered society boundary.</li>
            </ul>
          </div>
        </section>

        {/* Section 5 */}
        <section className={styles.section}>
          <h2 className={styles.sectionHeading}>
            <span className={styles.sectionNumber}>5</span> Data Retention & Account Deletion Policy
          </h2>
          <div className={styles.contentBlock}>
            <p>
              In full compliance with Google Play Data Safety standards, all users possess complete control over their personal data:
            </p>
            <ul className={styles.bulletList}>
              <li><strong>Visitor Log Retention:</strong> Gate entry logs and visitor records are automatically archived or purged according to the society&apos;s governance bylaws (typically 90 to 180 days).</li>
              <li><strong>Account & Data Deletion:</strong> If you relocate or vacate the property, you may request permanent deletion of your account and personal data at any time via:
                <br />
                <span className={styles.highlightPill}>In-App Settings &rarr; Profile &rarr; Delete Account</span> or by contacting our Data Protection Officer below.
              </li>
            </ul>
          </div>
        </section>

        {/* Section 6 */}
        <section className={styles.section}>
          <h2 className={styles.sectionHeading}>
            <span className={styles.sectionNumber}>6</span> Security Safeguards
          </h2>
          <div className={styles.contentBlock}>
            <p>
              We implement industry-standard administrative, physical, and technical safeguards including cryptographically salted password hashing, 
              stateless JWT authorization tokens with automatic invalidation upon credential change, strict cross-origin resource sharing (CORS) boundaries, 
              and automated audit logging for all privileged management actions.
            </p>
          </div>
        </section>

        {/* Contact Footer */}
        <section className={styles.contactSection}>
          <h2>Contact & Grievance Officer</h2>
          <p>If you have any questions, concerns, or requests regarding this Privacy Policy or your personal information, please contact our privacy compliance team:</p>
          <div className={styles.contactGrid}>
            <div className={styles.contactItem}>
              <Mail size={18} />
              <div>
                <strong>Email:</strong>
                <a href="mailto:support@myasmita.com">support@myasmita.com</a>
              </div>
            </div>
            <div className={styles.contactItem}>
              <MapPin size={18} />
              <div>
                <strong>Location:</strong>
                <span>Mumbai, Maharashtra, India</span>
              </div>
            </div>
            <div className={styles.contactItem}>
              <Shield size={18} />
              <div>
                <strong>Platform Portal:</strong>
                <a href="https://admin.myasmita.com">https://admin.myasmita.com</a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className={styles.legalFooter}>
        <div className={styles.footerContent}>
          <p>&copy; {new Date().getFullYear()} AsmitA Smart Home IoT & Society Operations. All rights reserved.</p>
          <div className={styles.footerLinks}>
            <Link to="/terms-conditions">Terms &amp; Conditions</Link>
            <Link to="/login">Sign In</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
